import React, { useCallback, memo, useRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { type FlagId, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import Ribbon from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/ribbon';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	CSAT_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { FeatureToggleTile } from '../../../../../common/ui/feature-toggle-tile/index.tsx';
import useSaveFailedFlag from '../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../controllers/common/service-project-features-controller/index.tsx';
import messages from './messages';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ChannelsFeatures = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();
	const [projectKey = ''] = usePathParam('projectKey');
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	const createOnChangeHandler = useCallback(
		(featureTitle: string) =>
			(feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });
				update({
					feature,
					enabled,
					onSuccess: () => {
						const flagIdKey = `${feature}-service-feature-success`;
						const { current: flagIds } = flagIdRef;
						// toFlagId creates a new string reference. But we want to reuse
						// flag ids so the most relevant flag for a feature is shown
						flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

						const id = flagIds[flagIdKey];
						// Dismiss previous flag with the same id. So user cannot
						// interact with out of update flags
						dismissFlag(id);

						showFlag({
							id,
							type: 'success',
							isAutoDismiss: !enabled,
							title: [messages.csatSuccessTitle, { value: enabled }],
							description: enabled
								? messages.successEnableDescription
								: messages.successDisableDescription,
							actions: enabled
								? [
										{
											content: messages.successEnableAction,
											onClick: () => {
												dismissFlag(id);
												push(`/jira/servicedesk/projects/${projectKey}/settings/feedback-settings`);
											},
										},
									]
								: [],
						});
					},
					onFail: () => {
						showSaveErrorFlag(featureTitle, enabled);
					},
				});
			},
		[projectKey, dismissFlag, push, showFlag, showSaveErrorFlag, update],
	);

	const inProductHelpArticleId = '3UP0988YCozC1pC59eZlo';
	const fallbackArticleUrl =
		'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-customer-satisfaction-surveys-csats/';
	const inProductHelpLink = (
		<HelpPanelLink
			appearance="link"
			spacing="none"
			articleId={inProductHelpArticleId}
			fallbackComponent={
				<a target="_blank" href={fallbackArticleUrl}>
					{formatMessage(messages.learnMoreAboutCsat)}
				</a>
			}
			onClick={() =>
				fireUIAnalytics(createAnalyticsEvent({}), 'helpLink clicked', 'learnMoreAboutCsat', {
					inProductHelpArticleId,
				})
			}
		>
			{formatMessage(messages.learnMoreAboutCsat)}
		</HelpPanelLink>
	);

	return (
		<FeatureToggleTile
			feature={CSAT_FEATURE}
			title={formatMessage(messages.csatTitle)}
			illustration={<Ribbon alt="" />}
			value={data?.[CSAT_FEATURE]?.status === ENABLED}
			saving={saving[CSAT_FEATURE]}
			onChange={createOnChangeHandler(formatMessage(messages.csatTitle))}
		>
			<FormattedMessage
				{...messages.csatDescriptionForInProductHelp}
				values={{
					learnMoreLink: inProductHelpLink,
					br: (
						<>
							<br />
							<br />
						</>
					),
				}}
			/>
		</FeatureToggleTile>
	);
});

export default ChannelsFeatures;
